/**
 * Gate 点的状态。
 *
 * 原来只看一件事：`actualDate` 填没填。填了就是绿点，没填就往后排，
 * 第一个没填的当「当前」。**跟计划日期到没到、逾期多久，完全没关系**——
 * 全库 131 个 GATE 里有 **26 个计划日期早就过了、还没通过**，界面上一点看不出来
 * （NS26009 的 GATE1 逾期 164 天，照样是个普通的空点）。这是 Tony 8/19 问的那个问题。
 *
 * 改成按**事实**上色，四种：
 *
 * | 点  | 含义       | 判据                               |
 * |-----|-----------|------------------------------------|
 * | 绿  | 已通过     | 有实际日期（晚于计划的另外标「晚 N 天」） |
 * | 黄  | 逾期未过   | 计划日期已过、还没有实际日期            |
 * | 蓝  | 进行中     | 第一个没过的，且还没到期               |
 * | 灰  | 待排期/未开始 | 没有计划日期，或还轮不到它            |
 *
 * **绿点不由系统算**。交付物完成率实测只有 1/122（X04C 122 个交付物只完成 1 个），
 * 拿它判断「够不够格过 Gate」会全线飘红，等于没信息；而且界面上早就写着
 * 「以上是事实统计，不代表 Gate 能否通过——放行与否由评审会决定」。
 * 所以绿 = 有人拍板过了，黄 = 日子过了还没拍板，都是事实，不替评审会做判断。
 */

export type GateTone = 'passed' | 'overdue' | 'current' | 'idle';

export interface GateMilestone {
  milestoneCode: string;
  planDate?: string | null;
  actualDate?: string | null;
  gateStatus?: string | null;
}

export interface GateView {
  code: string;
  title: string;
  planDate: string | null;
  actualDate: string | null;
  tone: GateTone;
  /** 点下面那行小字 */
  status: string;
  /** 逾期天数（黄点）或晚了几天（绿点）；不适用时是 0 */
  driftDays: number;
  completed: boolean;
  current: boolean;
}

export const GATE_DEFINITIONS: ReadonlyArray<{ code: string; title: string }> = [
  { code: 'GATE1', title: '项目立项' },
  { code: 'GATE2', title: '设计方案批准' },
  { code: 'GATE3', title: '产品与过程开发批准' },
  { code: 'GATE4', title: 'PPAP 批准' },
  { code: 'GATE5', title: '量产移交' },
  { code: 'GATE6', title: '项目关闭' },
];

/** 数据库 gate_status 原值 → 中文；未收录的原值原样显示，不静默吞掉 */
const GATE_STATUS_LABEL: Readonly<Record<string, string>> = {
  unscheduled: '待排期',
  scheduled: '已排期',
  pending: '待评审',
  passed: '已通过',
  conditional: '有条件通过',
  rejected: '未通过',
  waived: '例外放行',
};

/**
 * 里程碑那一行显示什么状态。
 *
 * 两条规矩：
 * - **不把库里的原值直接摆到界面上**。gate_status 存的是 'unscheduled' 这类英文枚举，
 *   原样显示就是中文界面里蹦出一个英文单词。项目概览页以前就是这样。
 * - **有计划日期就不能叫「待排期」**。存量 26 个 GATE6 的 gate_status 全是 'unscheduled'，
 *   其中 9 个明明有计划日期；GATE6 接上项目结束日期之后 26 个都会有日期。
 *   日期和状态互相打脸，人只会当这个状态是坏的、以后都不看它。
 *   gate_status 表达的是"评审过了没有"，排没排期看日期就够了。
 */
export function milestoneStatusLabel(
  milestone: { planDate: string | null; actualDate: string | null; gateStatus: string | null },
): string {
  if (milestone.actualDate) return '已完成';
  if (!milestone.planDate) return GATE_STATUS_LABEL[milestone.gateStatus ?? ''] ?? '待排期';
  const label = GATE_STATUS_LABEL[milestone.gateStatus ?? ''];
  if (!label || label === '待排期') return '计划中';
  return label;
}

const PASSED_STATUSES = new Set([
  'approved', 'completed', 'closed', 'passed', '已批准', '已完成', '已关闭',
]);

/** 只比日期，不比时刻——里程碑是按天定的，差几小时不算逾期 */
function toDay(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function daysBetween(from: number, to: number): number {
  return Math.round((to - from) / 86_400_000);
}

export function buildGates(
  milestones: GateMilestone[] = [],
  today: Date = new Date(),
): GateView[] {
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

  const merged = GATE_DEFINITIONS.map((definition) => {
    const milestone = milestones.find((item) =>
      String(item.milestoneCode ?? '').toUpperCase().replace(/[\s_-]/gu, '') === definition.code);
    const planDay = toDay(milestone?.planDate);
    const actualDay = toDay(milestone?.actualDate);
    const completed = Boolean(actualDay)
      || PASSED_STATUSES.has(String(milestone?.gateStatus ?? '').toLowerCase());

    let tone: GateTone = 'idle';
    let status: string;
    let driftDays = 0;

    if (completed) {
      tone = 'passed';
      // 晚了多少天要写出来：X04C 的 GATE2 晚了 15 天，原来绿点上一个字都没有
      driftDays = planDay !== null && actualDay !== null && actualDay > planDay
        ? daysBetween(planDay, actualDay) : 0;
      status = driftDays ? `已通过 · 晚 ${driftDays} 天` : '已通过';
    } else if (planDay !== null && planDay < todayDay) {
      tone = 'overdue';
      driftDays = daysBetween(planDay, todayDay);
      status = `逾期 ${driftDays} 天未通过`;
    } else {
      status = GATE_STATUS_LABEL[milestone?.gateStatus ?? '']
        ?? (planDay !== null ? '计划中' : '待排期');
    }

    return {
      ...definition,
      planDate: milestone?.planDate ?? null,
      actualDate: milestone?.actualDate ?? null,
      tone, status, driftDays, completed, current: false,
    };
  });

  // 「当前」= 第一个没通过的。它要是已经逾期，黄色优先——逾期比「进行中」更该被看见
  const currentIndex = merged.findIndex((gate) => !gate.completed);
  return merged.map((gate, index) => {
    const current = index === currentIndex;
    return {
      ...gate,
      current,
      tone: current && gate.tone === 'idle' ? 'current' : gate.tone,
    };
  });
}

/** 顶栏上一句话概括：有逾期就先说逾期 */
export function gateHeadline(gates: GateView[]): string {
  const overdue = gates.filter((gate) => gate.tone === 'overdue');
  if (overdue.length) {
    const worst = overdue.reduce((a, b) => (a.driftDays >= b.driftDays ? a : b));
    return `${worst.code} ${worst.title} 逾期 ${worst.driftDays} 天还没通过`;
  }
  const current = gates.find((gate) => gate.current);
  if (!current) return '六个 Gate 都通过了';
  return current.planDate ? `当前 ${current.code} ${current.title}` : `当前 ${current.code} ${current.title}，还没排期`;
}
